/**
 * postMessage protocol between the partner-site loader (@cesto/web-sdk)
 * and the invest popup (app.cesto.co/invest/[slug]).
 *
 * Every message is `{ cesto: 1, type, payload }`. The version literal lets a
 * future v2 coexist: unknown versions and types are dropped, never errored,
 * so an old loader can't break a new popup or vice versa.
 *
 * This package is intentionally dependency-free. The popup keeps its own zod
 * schemas; the guards here are narrow hand-rolled validators so the loader
 * ships with zero runtime dependencies.
 *
 * ⚠️ That means the wire contract is DUPLICATED across two repos: here, and in
 * `lib/invest-embed/protocol.ts` in the app. This package is unpublished, so
 * nothing links them — and because unknown types are dropped rather than
 * errored (by design, for version skew), a field added on one side only fails
 * SILENTLY, as a feature that just doesn't work.
 *
 * The event-name lists below are therefore locked by a contract test on each
 * side (`index.test.ts` here, `protocol.unit.test.ts` there). Changing a list
 * fails that side's test; update BOTH repos together.
 */

export const CESTO_PROTOCOL_VERSION = 1 as const;

/** popup to opener event names.
 *
 * Exported, and with the TYPE derived from the array rather than declared
 * alongside it: the two used to be maintained separately, so a new event could
 * be added to one and missed in the other. Exporting the array is also what
 * lets the contract test lock this list — see the drift warning at the top of
 * this file. */
export const POPUP_EVENT_TYPES = [
  'ready',
  'context:verified',
  'invest:success',
  'invest:scheduled',
  'invest:failed',
  'position:closed',
  'dialog:closed',
  'auth:handoff',
  'wallet:detect',
  'wallet:connect',
  'wallet:sign-message',
  'wallet:sign-transaction',
] as const;

export type PopupEventType = (typeof POPUP_EVENT_TYPES)[number];

/** popup to opener envelope. */
export interface PopupMessage {
  cesto: typeof CESTO_PROTOCOL_VERSION;
  type: PopupEventType;
  payload: Record<string, unknown>;
}

/** `invest:success` payload: the invest completed in this popup session. */
export interface InvestSuccessPayload {
  basket: string;
  amountUsd: number;
  status: string;
}

/** `invest:scheduled` payload: market closed, invest queued for open. */
export interface InvestScheduledPayload {
  basket: string;
  /** ISO timestamp when known; the popup may send null if unavailable. */
  scheduledFor: string | null;
}

/** `invest:failed` payload. */
export interface InvestFailedPayload {
  code: string;
  message: string;
}

/**
 * `position:closed` payload: the user closed a position inside the flow.
 *
 * Distinct from `dialog:closed`, which only means the surface was dismissed.
 * Unlike the `invest:*` events this is NOT once-per-session: a user can close
 * more than one position without leaving the dialog, and each closure is
 * reported.
 */
export interface PositionClosedPayload {
  basket: string;
  /** Product/version id of the closed position, when the app knows it. */
  positionId?: string;
  status: string;
}

/** `context:verified` payload: pk + origin validated server-side. */
export interface ContextVerifiedPayload {
  partner: string;
}

/**
 * `auth:handoff` payload: the flow moved to a top-level Cesto window.
 *
 * Some sign-in methods cannot run inside a frame at all — Google's consent
 * screen and `auth.privy.io` both answer `X-Frame-Options: DENY` — so the
 * dialog opens a window of its own and waits. Informational only: the dialog
 * stays up and still reports `invest:success` / `invest:failed` as usual.
 * Partners who show their own "signing you in…" state want to know.
 */
export interface AuthHandoffPayload {
  /** Why the flow left the frame. Open-ended: more methods may follow. */
  reason: 'google-login';
}

/**
 * Wallet relay: browser-wallet extensions never inject providers into
 * iframes, so the invest dialog asks the host (the SDK, running top-level
 * on the partner page) to detect/connect/sign on its behalf. Every request
 * carries an opaque correlation `id` that the host echoes verbatim, and
 * every request gets exactly one response (success or `wallet:error`).
 */

/** `wallet:detect` request payload (popup to host). */
export interface WalletDetectPayload {
  id: string;
}

/** `wallet:connect` request payload (popup to host). */
export interface WalletConnectPayload {
  id: string;
  walletId: string;
}

/** `wallet:sign-message` request payload (popup to host). */
export interface WalletSignMessagePayload {
  id: string;
  walletId: string;
  message: string;
}

/** `wallet:sign-transaction` request payload (popup to host). The transaction
 * travels as base64-serialized bytes; signing happens raw-bytes via the
 * Wallet Standard, so no web3.js dependency enters the loader. `address`
 * selects the signing account when the wallet exposes several. */
export interface WalletSignTransactionPayload {
  id: string;
  walletId: string;
  /** base64-encoded serialized (unsigned) transaction. */
  transaction: string;
  /** base58 address expected to sign (the SIWS login wallet). */
  address?: string;
}

/** host to popup event names (wallet relay responses). */
export const HOST_EVENT_TYPES = [
  'wallet:detected',
  'wallet:address',
  'wallet:signature',
  'wallet:transaction',
  'wallet:error',
] as const;

export type HostEventType = (typeof HOST_EVENT_TYPES)[number];

/** host to popup envelope (wallet relay responses). */
export interface HostMessage {
  cesto: typeof CESTO_PROTOCOL_VERSION;
  type: HostEventType;
  payload: Record<string, unknown>;
}

/** `wallet:detected` payload: extensions present on the partner page. */
export interface WalletDetectedPayload {
  id: string;
  wallets: { id: string; name: string }[];
}

/** `wallet:address` payload: connected wallet address, base58. */
export interface WalletAddressPayload {
  id: string;
  address: string;
}

/** `wallet:signature` payload: signed message signature, base58. */
export interface WalletSignaturePayload {
  id: string;
  signature: string;
}

/** `wallet:transaction` payload: the SIGNED transaction, base64-serialized. */
export interface WalletTransactionPayload {
  id: string;
  transaction: string;
}

/** `wallet:error` payload: the relay request failed. */
export interface WalletErrorPayload {
  id: string;
  code: string;
  message: string;
}

/** opener to popup init payload. */
export interface InitPayload {
  /** cesto_pk_... key. The ?pk= URL param is AUTHORITATIVE — it is what the
   * app's middleware computed `frame-ancestors` from. This field is kept for
   * older loaders; when both are present the popup requires them to MATCH and
   * otherwise refuses to verify (SEC-D). Never send a different key here. */
  publishableKey?: string;
  /** Basket slug the partner thinks it opened; the URL path is authoritative. */
  basket?: string;
  prefill?: {
    amountUsd?: number;
  };
}

/**
 * opener to popup. The first (and only) message the popup accepts; its
 * `event.origin` is pinned as the partner origin for the whole session.
 */
export interface InitMessage {
  cesto: typeof CESTO_PROTOCOL_VERSION;
  type: 'init';
  payload: InitPayload;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `includes` on an `as const` tuple only accepts the literal union, but the
 * whole point of these guards is testing an arbitrary inbound string. Widen at
 * the lookup rather than dropping `as const`, which is what makes the exported
 * arrays usable as the contract lock. */
function includesName(names: readonly string[], value: string): boolean {
  return names.includes(value);
}

/**
 * Narrow guard for messages arriving from the popup. Checks the version
 * literal, that `type` is a known popup event, and that `payload` is a plain
 * object. Anything else (including future protocol versions) returns false
 * and must be silently dropped by the caller.
 */
export function isPopupMessage(data: unknown): data is PopupMessage {
  if (!isRecord(data)) return false;
  return (
    data.cesto === CESTO_PROTOCOL_VERSION &&
    typeof data.type === 'string' &&
    includesName(POPUP_EVENT_TYPES, data.type) &&
    isRecord(data.payload)
  );
}

/**
 * Narrow guard for wallet relay responses arriving from the host. Same
 * drop-never-error contract as `isPopupMessage`.
 */
export function isHostMessage(data: unknown): data is HostMessage {
  if (!isRecord(data)) return false;
  return (
    data.cesto === CESTO_PROTOCOL_VERSION &&
    typeof data.type === 'string' &&
    includesName(HOST_EVENT_TYPES, data.type) &&
    isRecord(data.payload)
  );
}

/** Builds the versioned init envelope the loader sends on `ready`. */
export function buildInitMessage(payload: InitPayload): InitMessage {
  return { cesto: CESTO_PROTOCOL_VERSION, type: 'init', payload };
}

/** Builds a versioned wallet relay response envelope (host to popup). */
export function buildHostMessage(
  type: HostEventType,
  payload: Record<string, unknown>,
): HostMessage {
  return { cesto: CESTO_PROTOCOL_VERSION, type, payload };
}
